/**
 * La grille des shifts du cockpit — la SEULE définition écrite dans ce dépôt.
 *
 * Kayla, 12-09-2026 : quatre créneaux de 6 h, à l'heure pleine, ancrés sur
 * 03:00, en heure de PARIS (`FUSEAU_DES_SHIFTS`) :
 *   A · Petit matin 03:00–09:00 · B · Matin 09:00–15:00 ·
 *   C · Après-midi 15:00–21:00 · D · Nuit 21:00–03:00.
 *
 * Kayla, 29-10-2026 : « des fois les gens demandent le planning ; Francis,
 * s'il envoie une image avec des horaires, sur le cockpit ça dit autre chose ».
 * Jusqu'ici la grille était écrite DEUX fois dans ce dépôt — `panels/
 * organisation/shiftsFixes.ts` (les noms, pour l'écran de Kayla, sans lettre)
 * et `regie-test/constants.ts` (les lettres, pour le formulaire candidat) —
 * et le chatteur, lui, ne lisait que des heures. Désormais les deux dérivent
 * d'ici, et chaque surface dit la LETTRE avec les heures : l'onglet Shift du
 * chatteur, la semaine de Kayla, la fiche de candidature, l'image du planning
 * et les faits de Francis.
 *
 * ⚠️ Ce que Francis LIT à chaque tour n'est pas cette constante : c'est le nom
 * des vocaux Discord (« 🔊 Vocal — Shift B (09h00–15h00) »,
 * `convex/_lib/ticketsAgent.creneauxDeLaGrille`), la source qui porte aussi
 * les rôles. Le tour compare les deux et le dit dans ses logs quand elles
 * divergent (`memeGrilleQueLesVocaux`). Les autres copies, hors de ce dépôt :
 * `app/manager/grille.py` côté Cyrano (les lettres du formulaire, épinglées en
 * clair par son test) et `SHIFT_MIN` du bot Cerbère sur le VDS (les rappels).
 *
 * Avant : six de 4 h ancrés sur 08:30 (18-08 → 12-09-2026), et avant encore
 * quatre de 6 h ancrés sur 08:00. Ce qui ne bouge pas d'une grille à l'autre :
 * des bornes rondes ET des durées égales — c'est le NOMBRE de shifts qui plie.
 */

export interface CreneauFixe {
  /** La clé stockée dans les fiches de candidature (`availability`) : la lettre en minuscule. */
  key: "a" | "b" | "c" | "d";
  /** La lettre que Discord, Francis et les chatteurs nomment. */
  lettre: string;
  /** Le nom que Kayla lit sur sa semaine. */
  nom: string;
  /** Minutes depuis minuit, heure de Paris. `endMinute <= startMinute` : il traverse minuit. */
  startMinute: number;
  endMinute: number;
}

export const GRILLE_DES_SHIFTS: readonly CreneauFixe[] = [
  { key: "a", lettre: "A", nom: "Petit matin", startMinute: 3 * 60, endMinute: 9 * 60 },
  { key: "b", lettre: "B", nom: "Matin", startMinute: 9 * 60, endMinute: 15 * 60 },
  { key: "c", lettre: "C", nom: "Après-midi", startMinute: 15 * 60, endMinute: 21 * 60 },
  { key: "d", lettre: "D", nom: "Nuit", startMinute: 21 * 60, endMinute: 3 * 60 },
] as const;

export interface BornesDuCreneau {
  startMinute: number;
  endMinute: number;
}

/**
 * La lettre d'un bloc, appariée sur ses DEUX bornes — « 09:00–15:00 » → « B ».
 * Un bloc sur mesure n'a pas de lettre (`null`) ; on ne le range jamais de
 * force dans le créneau le plus proche (D finit à 03:00, A commence à 03:00 :
 * une seule borne les mélangerait).
 */
export function lettreDuCreneau(
  bornes: BornesDuCreneau,
  grille: ReadonlyArray<BornesDuCreneau & { lettre: string }> = GRILLE_DES_SHIFTS,
): string | null {
  return grille.find((c) => c.startMinute === bornes.startMinute && c.endMinute === bornes.endMinute)?.lettre ?? null;
}

/** « A · Petit matin » : ce que Kayla lit sur sa semaine, la lettre d'abord. */
export function libelleDuCreneau(c: Pick<CreneauFixe, "lettre" | "nom">): string {
  return `${c.lettre} · ${c.nom}`;
}

/**
 * Deux grilles disent-elles la MÊME chose ? Mêmes lettres, mêmes bornes —
 * l'ordre et les noms ne comptent pas. Sert au tour de Francis : la grille des
 * vocaux Discord contre celle-ci.
 */
export function memeGrilleQueLesVocaux(
  vocaux: ReadonlyArray<BornesDuCreneau & { lettre: string }>,
  grille: ReadonlyArray<BornesDuCreneau & { lettre: string }> = GRILLE_DES_SHIFTS,
): boolean {
  if (vocaux.length !== grille.length) return false;
  const cle = (c: BornesDuCreneau & { lettre: string }) => `${c.lettre.toUpperCase()}:${c.startMinute}-${c.endMinute}`;
  const a = new Set(vocaux.map(cle));
  return grille.every((c) => a.has(cle(c)));
}
